import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Search, Trash2, Edit2, Tag, X, Clock, FileText } from 'lucide-react';
import ConfirmModal from '../components/ConfirmModal';
import { useToast } from '../components/Toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Helper to get auth headers
const getAuthHeaders = () => {
  const stored = sessionStorage.getItem('assistant');
  if (stored) {
    try {
      const assistant = JSON.parse(stored);
      return { 'x-assistant-session': assistant.id };
    } catch (e) {}
  }
  return {};
};

const NotesPage = () => {
  const navigate = useNavigate();
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedNote, setSelectedNote] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [noteToDelete, setNoteToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const { success, error: showError } = useToast();
  
  const [formData, setFormData] = useState({
    title: '',
    body: '',
    tags: [],
    visibility: 'assistant_only'
  });
  const [tagInput, setTagInput] = useState('');

  useEffect(() => {
    fetchNotes();
    
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const fetchNotes = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${BACKEND_URL}/api/assistant/notes`, {
        headers: getAuthHeaders(),
        
      });
      
      if (res.ok) {
        const data = await res.json();
        setNotes(data.notes || []);
      } else if (res.status === 401) {
        navigate('/assistant-login');
      }
      setLoading(false);
    } catch (err) {
      console.error('Error fetching notes:', err);
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!formData.title.trim()) {
      showError('Title required', 'Please enter a title for your note');
      return;
    }

    try {
      const res = await fetch(`${BACKEND_URL}/api/assistant/notes`, {
        headers: getAuthHeaders(),
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        
        body: JSON.stringify(formData)
      });

      const data = await res.json();
      if (data.ok) {
        success('Note created', 'Your note has been saved');
        setShowCreateModal(false);
        resetForm();
        fetchNotes();
      } else {
        showError('Error', data.error || 'Failed to create note');
      }
    } catch (err) {
      console.error('Create note error:', err);
      showError('Error', 'Failed to create note');
    }
  };

  const handleUpdate = async () => {
    if (!formData.title.trim()) {
      showError('Title required', 'Please enter a title for your note');
      return;
    }

    try {
      const res = await fetch(`${BACKEND_URL}/api/assistant/notes/${selectedNote.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        
        body: JSON.stringify(formData)
      });

      const data = await res.json();
      if (data.ok) {
        success('Note updated', 'Your changes have been saved');
        setShowEditModal(false);
        setSelectedNote(null);
        resetForm();
        fetchNotes();
      } else {
        showError('Error', data.error || 'Failed to update note');
      }
    } catch (err) {
      console.error('Update note error:', err);
      showError('Error', 'Failed to update note');
    }
  };

  const handleDelete = async () => {
    if (!noteToDelete) return;
    
    setIsDeleting(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/assistant/notes/${noteToDelete.id}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });

      const data = await res.json();
      if (data.ok) {
        success('Note deleted', 'Note has been removed');
        setShowDeleteConfirm(false);
        setNoteToDelete(null);
        fetchNotes();
      } else {
        showError('Error', 'Failed to delete note');
      }
    } catch (err) {
      console.error('Delete note error:', err);
      showError('Error', 'Failed to delete note');
    } finally {
      setIsDeleting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      body: '',
      tags: [],
      visibility: 'assistant_only'
    });
    setTagInput('');
  };

  const openEditModal = (note) => {
    setSelectedNote(note);
    setFormData({
      title: note.title,
      body: note.body,
      tags: note.tags || [],
      visibility: note.visibility || 'assistant_only'
    });
    setShowEditModal(true);
  };

  const addTag = () => {
    if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
      setFormData({ ...formData, tags: [...formData.tags, tagInput.trim()] });
      setTagInput('');
    }
  };

  const removeTag = (tag) => {
    setFormData({ ...formData, tags: formData.tags.filter(t => t !== tag) });
  };

  const filteredNotes = notes.filter(note => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      note.title.toLowerCase().includes(query) ||
      note.body.toLowerCase().includes(query) ||
      (note.tags || []).some(t => t.toLowerCase().includes(query))
    );
  });

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: '#F8F9FA', padding: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <p style={{ color: '#6B7280' }}>Loading notes...</p>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: '#F8F9FA' }}>
      {/* Header */}
      <div style={{ background: '#FFFFFF', borderBottom: '1px solid #E5E7EB', padding: isMobile ? '12px 16px' : '20px 32px' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              <button
                onClick={() => navigate('/a-dashboard')}
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '8px', borderRadius: '8px' }}
              >
                <ArrowLeft size={20} color="#374151" />
              </button>
              <div>
                <h1 style={{ color: '#0A0A0A', fontSize: isMobile ? '18px' : '24px', fontWeight: '600', margin: 0 }}>
                  Notes
                </h1>
                <p style={{ color: '#6B7280', fontSize: '13px', margin: '4px 0 0 0' }}>
                  {notes.length} note{notes.length !== 1 ? 's' : ''}
                </p>
              </div>
            </div>
            
            <button
              onClick={() => { resetForm(); setShowCreateModal(true); }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '10px 16px',
                background: '#0A0A0A',
                color: '#FFFFFF',
                border: 'none',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer'
              }}
            >
              <Plus size={16} />
              New Note
            </button>
          </div>

          {/* Search */}
          <div style={{ marginTop: '16px', position: 'relative' }}>
            <Search size={18} color="#9CA3AF" style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)' }} />
            <input
              type="text"
              placeholder="Search notes..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{
                width: '100%',
                padding: '10px 12px 10px 40px',
                border: '1px solid #E5E7EB',
                borderRadius: '8px',
                fontSize: '14px',
                background: '#F9FAFB'
              }}
            />
          </div>
        </div>
      </div>

      {/* Notes Grid */}
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: isMobile ? '16px' : '24px 32px' }}>
        {filteredNotes.length === 0 ? (
          <div style={{ 
            background: '#FFFFFF', 
            borderRadius: '12px', 
            border: '1px solid #E5E7EB',
            padding: '48px 24px',
            textAlign: 'center'
          }}>
            <FileText size={48} color="#E5E7EB" style={{ margin: '0 auto 16px' }} />
            <h3 style={{ color: '#374151', fontSize: '16px', fontWeight: '500', margin: '0 0 8px 0' }}>
              {searchQuery ? 'No notes found' : 'No notes yet'}
            </h3>
            <p style={{ color: '#6B7280', fontSize: '14px', margin: 0 }}>
              {searchQuery ? 'Try a different search term' : 'Create your first note to get started'}
            </p>
          </div>
        ) : (
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(320px, 1fr))',
            gap: '16px'
          }}>
            {filteredNotes.map(note => (
              <div
                key={note.id}
                style={{
                  background: '#FFFFFF',
                  borderRadius: '12px',
                  border: '1px solid #E5E7EB',
                  padding: '16px',
                  cursor: 'pointer',
                  transition: 'box-shadow 0.2s'
                }}
                onClick={() => openEditModal(note)}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                  <h3 style={{ 
                    color: '#0A0A0A', 
                    fontSize: '15px', 
                    fontWeight: '600', 
                    margin: 0,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    maxWidth: '200px'
                  }}>
                    {note.title}
                  </h3>
                  <div style={{ display: 'flex', gap: '4px' }}>
                    <button
                      onClick={(e) => { e.stopPropagation(); openEditModal(note); }}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px' }}
                    >
                      <Edit2 size={14} color="#6B7280" />
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); setNoteToDelete(note); setShowDeleteConfirm(true); }}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px' }}
                    >
                      <Trash2 size={14} color="#DC2626" />
                    </button>
                  </div>
                </div>
                
                <p style={{ 
                  color: '#6B7280', 
                  fontSize: '13px', 
                  margin: '0 0 12px 0',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  display: '-webkit-box',
                  WebkitLineClamp: 3,
                  WebkitBoxOrient: 'vertical',
                  lineHeight: '1.5'
                }}>
                  {note.body || 'No content'}
                </p>

                {/* Tags */}
                {note.tags && note.tags.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginBottom: '12px' }}>
                    {note.tags.slice(0, 3).map(tag => (
                      <span
                        key={tag}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '4px',
                          padding: '2px 8px',
                          background: '#F3F4F6',
                          borderRadius: '4px',
                          fontSize: '11px',
                          color: '#6B7280'
                        }}
                      >
                        <Tag size={10} />
                        {tag}
                      </span>
                    ))}
                    {note.tags.length > 3 && (
                      <span style={{ fontSize: '11px', color: '#9CA3AF' }}>
                        +{note.tags.length - 3} more
                      </span>
                    )}
                  </div>
                )}

                <div style={{ display: 'flex', alignItems: 'center', gap: '4px', color: '#9CA3AF', fontSize: '11px' }}>
                  <Clock size={12} />
                  {formatDate(note.updated_at)}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      {(showCreateModal || showEditModal) && (
        <div style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 50,
          padding: '16px'
        }}>
          <div style={{
            background: '#FFFFFF',
            borderRadius: '16px',
            width: '100%',
            maxWidth: '600px',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <div style={{ padding: '20px', borderBottom: '1px solid #E5E7EB' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h2 style={{ color: '#0A0A0A', fontSize: '18px', fontWeight: '600', margin: 0 }}>
                  {showEditModal ? 'Edit Note' : 'New Note'}
                </h2>
                <button
                  onClick={() => { setShowCreateModal(false); setShowEditModal(false); setSelectedNote(null); }}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px' }}
                >
                  <X size={20} color="#6B7280" />
                </button>
              </div>
            </div>

            <div style={{ padding: '20px' }}>
              {/* Title */}
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', color: '#374151', fontSize: '13px', fontWeight: '500', marginBottom: '6px' }}>
                  Title *
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Enter note title"
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #D1D5DB',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}
                />
              </div>

              {/* Body */}
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', color: '#374151', fontSize: '13px', fontWeight: '500', marginBottom: '6px' }}>
                  Content
                </label>
                <textarea
                  value={formData.body}
                  onChange={(e) => setFormData({ ...formData, body: e.target.value })}
                  placeholder="Write your note here..."
                  rows={10}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #D1D5DB',
                    borderRadius: '8px',
                    fontSize: '14px',
                    resize: 'vertical',
                    fontFamily: 'inherit',
                    lineHeight: '1.6'
                  }}
                />
              </div>

              {/* Tags */}
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', color: '#374151', fontSize: '13px', fontWeight: '500', marginBottom: '6px' }}>
                  Tags
                </label>
                <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                  <input
                    type="text"
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                    placeholder="Add tag..."
                    style={{
                      flex: 1,
                      padding: '8px 12px',
                      border: '1px solid #D1D5DB',
                      borderRadius: '8px',
                      fontSize: '14px'
                    }}
                  />
                  <button
                    onClick={addTag}
                    style={{
                      padding: '8px 16px',
                      background: '#F3F4F6',
                      border: '1px solid #D1D5DB',
                      borderRadius: '8px',
                      fontSize: '14px',
                      cursor: 'pointer'
                    }}
                  >
                    Add
                  </button>
                </div>
                {formData.tags.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                    {formData.tags.map(tag => (
                      <span
                        key={tag}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '6px',
                          padding: '4px 10px',
                          background: '#E5E7EB',
                          borderRadius: '6px',
                          fontSize: '12px',
                          color: '#374151'
                        }}
                      >
                        {tag}
                        <button
                          onClick={() => removeTag(tag)}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex' }}
                        >
                          <X size={12} color="#6B7280" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* Visibility */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', color: '#374151', fontSize: '13px', fontWeight: '500', marginBottom: '6px' }}>
                  Visibility
                </label>
                <select
                  value={formData.visibility}
                  onChange={(e) => setFormData({ ...formData, visibility: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #D1D5DB',
                    borderRadius: '8px',
                    fontSize: '14px',
                    background: '#FFFFFF'
                  }}
                >
                  <option value="assistant_only">Private (Only me)</option>
                  <option value="shared_with_exec">Shared with Executive</option>
                </select>
              </div>

              {/* Actions */}
              <div style={{ display: 'flex', gap: '12px' }}>
                <button
                  onClick={() => { setShowCreateModal(false); setShowEditModal(false); setSelectedNote(null); }}
                  style={{
                    flex: 1,
                    padding: '10px',
                    background: 'transparent',
                    color: '#6B7280',
                    border: '1px solid #D1D5DB',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: '500',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={showEditModal ? handleUpdate : handleCreate}
                  style={{
                    flex: 1,
                    padding: '10px',
                    background: '#0A0A0A',
                    color: '#FFFFFF',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: '500',
                    cursor: 'pointer'
                  }}
                >
                  {showEditModal ? 'Save Changes' : 'Create Note'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation */}
      <ConfirmModal
        isOpen={showDeleteConfirm}
        onClose={() => { setShowDeleteConfirm(false); setNoteToDelete(null); }}
        onConfirm={handleDelete}
        title="Delete Note"
        message={`Are you sure you want to delete "${noteToDelete?.title}"? This cannot be undone.`}
        confirmText="Delete"
        confirmStyle="danger"
        isLoading={isDeleting}
      />
    </div>
  );
};

export default NotesPage;
